import {
  CallParticipantCapability,
  ClientStatus,
  isCapableOf,
  ProducerLabel,
  Role,
  UserStatus,
} from "@ameelio/connect-call-client";
import GroupsIcon from "@mui/icons-material/Groups";
import { Box, useMediaQuery as measureScreenWidth, Stack } from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";
import useChange from "@react-hook/change";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSound } from "use-sound";
import { CallFeature, CallParticipant, Interval } from "../../api/graphql";
import blurBackgroundEnabled from "../../lib/blurBackgroundEnabled";
import isAmeelioTablet from "../../lib/isAmeelioTablet";
import { largeTabletAndUp } from "../../lib/responsiveHelpers";
import { isConfidentialCall } from "../../lib/securityFeaturesUtils.ts";
import {
  DroppedStreams,
  useStreamTrackerContext,
} from "../../lib/StreamTrackerProvider";
import { DeviceInfo, useDeviceChanges } from "../../lib/useDevices";
import useMediaPermissions from "../../lib/useMediaPermissions";
import { darkPalette, ltrTheme } from "../../theme";
import CallBackgroundSelector from "./CallBackgroundSelector";
import CallChat from "./CallChat";
import CallConfidentialWidget from "./CallConfidentialWidget";
import CallControls from "./CallControls";
import { CallLayoutMode, getDefaultLayoutMode } from "./CallLayoutMode";
import CallPanel, { chatWidth } from "./CallPanel";
import CallParticipants from "./CallParticipants";
import CallTimerWidget from "./CallTimerWidget";
import CallWidget from "./CallWidget";
import CaptionBox from "./CaptionBox";
import CaptionsContainer from "./CaptionsContainer";
import FloatingSelfieVideo from "./FloatingSelfieVideo";
import ParticipantAudio from "./ParticipantAudio";
import { useSegmenterContext } from "./SegmenterProvider";
import chatRecvSound from "./sounds/chat_receive.mp3";
import SpotlightPlaceholder from "./SpotlightPlaceholder";
import StatusWidget from "./StatusWidget";
import {
  ConnectionInfo,
  LocalParticipant,
  Peer,
  RemoteParticipant,
} from "./types/ConnectionInfo";
import CustomBackground from "./types/CustomBackground";
import useCallAlerts from "./useCallAlerts";
import useResponsiveVideoGridDimensions from "./useResponsiveVideoGridDimensions";
import useSpotlightVideo from "./useSpotlightVideo";
import useUnreadMessageCount from "./useUnreadMessageCount";
import VideoParticipant from "./VideoParticipant";
import VideosGrid from "./VideosGrid";
import WebinarHostQAndA from "./WebinarHostQAndA";
import WebinarQAndA from "./WebinarQAndA";

function useUnreadEventCount(timestamps: number[], eventsVisible: boolean) {
  const [readTime, setReadTime] = useState(0);

  useEffect(() => {
    const maxEventTime = timestamps.reduce((a, b) => (a > b ? a : b), 0);
    if (eventsVisible) setReadTime(maxEventTime);
  }, [timestamps, eventsVisible]);

  return timestamps.filter((x) => x > readTime).length;
}

// Plays a sound whenever a new announcement, question, or raised hand
// appears, so a moderator is alerted even while the panel is closed.
function useNewQAndAAlertSound(timestamps: number[]) {
  const [playSound] = useSound(chatRecvSound, { volume: 0.5 });
  const lastSeenTime = useRef(timestamps.reduce((a, b) => (a > b ? a : b), 0));

  useEffect(() => {
    const maxEventTime = timestamps.reduce(
      (a, b) => (a > b ? a : b),
      lastSeenTime.current
    );
    if (maxEventTime > lastSeenTime.current) playSound();
    lastSeenTime.current = maxEventTime;
  }, [timestamps, playSound]);
}

enum Panel {
  None,
  Chat,
  Participants,
  Backgrounds,
}

const canBlurBackground = blurBackgroundEnabled();

const backdropStyle: SystemStyleObject<Theme> = {
  px: { xs: 1, md: 2 },
  py: { xs: 1, md: 2 },
  height: "100%",
  backgroundColor: darkPalette.background.default,
  overflow: "hidden",
  position: "relative",
};

export type Props = {
  facilityName: string;

  // User
  currentParticipant: Pick<CallParticipant, "externalId" | "fullName" | "type">;

  features: CallFeature[];

  // All participants
  participants: Pick<CallParticipant, "externalId" | "fullName">[];

  // Connection information
  connectionInfo: ConnectionInfo;

  interval: Pick<Interval, "startAt" | "endAt">;
  onPeerJoined: () => void;
  onLeave: () => void;
  leaving?: boolean;
  beginMuted: boolean;
  beginHidden: boolean;

  canChooseBackground: boolean;
  canRemoveBackground: boolean;
  customBackground?: CustomBackground;
  onSetCustomBackground?: (c: CustomBackground) => void;
};

function SuperVideoCallInterface({
  facilityName,
  connectionInfo,
  currentParticipant,
  participants,
  features,
  interval,
  onPeerJoined,
  onLeave,
  leaving,
  beginMuted,
  beginHidden,
  canChooseBackground,
  canRemoveBackground,
  customBackground,
  onSetCustomBackground,
}: Props) {
  const { t } = useTranslation();

  const participantsById = Object.fromEntries(
    participants.map((c) => [c.externalId, c])
  );
  // A couple of legacy panels (WebinarQAndA/WebinarHostQAndA) still expect
  // an `{id, fullName}[]` correspondent list rather than `externalId`.
  const correspondents = useMemo(
    () => participants.map((p) => ({ id: p.externalId, fullName: p.fullName })),
    [participants]
  );

  const {
    messages,
    sendMessage,

    sendWebinarQuestion,
    dismissWebinarQuestion,
    sendWebinarAnnouncement,
    dismissWebinarAnnouncement,
    remoteAudioMute,
    remoteAudioUnmute,

    raiseHand,
    lowerHand,
    remoteLowerHand,

    webinarQuestions,
    webinarAnnouncements,

    onScreenshare,

    callStatus,
    clientStatus,

    localProducers,
    pauseProducer,
    resumeProducer,

    toggleCaption,

    logClientEvent,
    peers,

    captionsTimeline,

    enableFrux,

    callFeatures,

    user: receivedUser,
  } = connectionInfo;

  useCallAlerts(messages);

  // Enable frux immediately
  useEffect(() => {
    if (enableFrux) enableFrux();
  }, [enableFrux]);

  const onDeviceAdded = useCallback(
    (device: DeviceInfo) => {
      if (clientStatus === ClientStatus.connected) {
        logClientEvent(`Device added: ${device.label} - ${device.kind}`);
      }
    },
    [logClientEvent, clientStatus]
  );
  const onDeviceRemoved = useCallback(
    (device: DeviceInfo) => {
      if (clientStatus === ClientStatus.connected) {
        logClientEvent(`Device removed: ${device.label} - ${device.kind}`);
      }
    },
    [logClientEvent, clientStatus]
  );
  useDeviceChanges(onDeviceAdded, onDeviceRemoved);

  const { cameraDisabled, microphoneDisabled } = useMediaPermissions();
  useEffect(() => {
    if (clientStatus === ClientStatus.connected) {
      logClientEvent(
        `Device availability (status: ${clientStatus}) - Camera: ${!cameraDisabled} - Microphone: ${!microphoneDisabled}`
      );
    }
  }, [cameraDisabled, microphoneDisabled, clientStatus, logClientEvent]);

  // Staff monitors join as regular peers but must never be shown as
  // participants (no tile, no count, not in the panel). Monitor-specific
  // event handling still runs off `peers` upstream.
  const nonMonitorPeers = useMemo(
    () => Object.values(peers).filter((p) => p.user.role !== Role.monitor),
    [peers]
  );

  // Peers with ISOLATION are hidden from every other attendee's video tiles
  // (this is what today's "isolated webinar attendee" already means) — but
  // hosts/moderators (REMOTE_CONTROL) still need to see/mute them, and they
  // still appear in the participants panel below regardless.
  const canSeeIsolatedPeers = isCapableOf(
    CallParticipantCapability.REMOTE_CONTROL,
    receivedUser
  );
  const visiblePeers = useMemo(
    () =>
      nonMonitorPeers.filter(
        (p) =>
          canSeeIsolatedPeers ||
          !isCapableOf(CallParticipantCapability.ISOLATION, p)
      ),
    [nonMonitorPeers, canSeeIsolatedPeers]
  );

  const streamTracker = useStreamTrackerContext();
  const [peerHasConnected, setPeerHasConnected] = useState<boolean>(false);
  const [shownPanel, setShownPanel] = useState<Panel>(Panel.None);
  const [showCaptions, setShowCaptions] = useState<boolean>(false);

  const isLargeTabletAndUp = largeTabletAndUp(measureScreenWidth);

  // Track last known set of unjoined peers
  // and trigger onPeerJoined event when a new one appears.
  const knownPeers = useRef<Set<string>>(new Set());
  useEffect(() => {
    Object.keys(visiblePeers).forEach((key) => {
      if (!knownPeers.current.has(key)) onPeerJoined();
    });
    knownPeers.current = new Set(Object.keys(visiblePeers));
  }, [visiblePeers, onPeerJoined]);

  useChange(
    receivedUser?.connectionState?.badConnection,
    (isDisabled: boolean | undefined): void => {
      if (isDisabled) streamTracker.handleDrop(DroppedStreams.local);
    }
  );

  const peerExists = visiblePeers.length > 0;
  useEffect(() => {
    if (!peerHasConnected) setPeerHasConnected(peerExists);
  }, [peerExists, peerHasConnected]);

  const { status: segmenterStatus } = useSegmenterContext();

  // Screensharing
  const [isScreensharing, setIsScreensharing] = useState<boolean>(false);
  const [screensharingTrack, setScreensharingTrack] =
    useState<MediaStreamTrack | null>(null);
  const peerIsScreensharing = visiblePeers.some(
    (p) =>
      p.consumers.screenshare?.stream &&
      !!p.consumers.screenshare?.stream.getVideoTracks().length
  );

  const toggleScreenshare = useCallback(async () => {
    if (onScreenshare && !isScreensharing) {
      const track = await onScreenshare();
      if (track) {
        track.addEventListener("ended", () => {
          setIsScreensharing(false);
          setScreensharingTrack(null);
        });
        setIsScreensharing(true);
        setScreensharingTrack(track);
      }
    } else if (screensharingTrack) {
      screensharingTrack.stop();
      screensharingTrack.dispatchEvent(new Event("ended"));
    }
  }, [onScreenshare, screensharingTrack, isScreensharing]);

  // Layout mode: defaults from participant count/screensharing, but any
  // participant can override their own view via CallControls.
  const defaultLayoutMode = useMemo(
    () => getDefaultLayoutMode(visiblePeers.length, isScreensharing),
    [visiblePeers.length, isScreensharing]
  );
  const [layoutModeOverride, setLayoutModeOverride] =
    useState<CallLayoutMode | null>(null);
  // Screensharing forces Spotlight (so the shared screen stays legible),
  // regardless of any manual override.
  const layoutMode =
    isScreensharing || peerIsScreensharing
      ? CallLayoutMode.Spotlight
      : (layoutModeOverride ?? defaultLayoutMode);
  const toggleLayoutMode = useCallback(() => {
    setLayoutModeOverride(
      layoutMode === CallLayoutMode.Grid
        ? CallLayoutMode.Spotlight
        : CallLayoutMode.Grid
    );
  }, [layoutMode]);

  const canModerate =
    isCapableOf(CallParticipantCapability.REMOTE_CONTROL, receivedUser) ||
    isCapableOf(CallParticipantCapability.ANNOUNCEMENTS, receivedUser);

  // Participants get a "(host)" tag on their video label when someone
  // else can moderate them but they can't moderate back.
  const getPeerNameTag = useCallback(
    (peer: Peer, peerName: string) => {
      const peerIsModerator = isCapableOf(
        CallParticipantCapability.REMOTE_CONTROL,
        peer
      );
      const hostTag = !canModerate && peerIsModerator ? t("(host)") : "";
      return peerIsModerator ? `${peerName} ${hostTag}`.trim() : peerName;
    },
    [t, canModerate]
  );

  const sortedPeers: RemoteParticipant[] = useMemo(
    () =>
      visiblePeers.map((p) => {
        const participant = participantsById[p.user.id] || { fullName: "" };
        return {
          ...p,
          name: participant,
          label: getPeerNameTag(p, participant.fullName),
        };
      }),
    [visiblePeers, participantsById, getPeerNameTag]
  );

  const selfAsParticipant: LocalParticipant = useMemo(
    () => ({
      isLocal: true,
      user: {
        id: currentParticipant.externalId,
        fullName: currentParticipant.fullName,
      },
    }),
    [currentParticipant]
  );

  const spotlitParticipant = useSpotlightVideo({
    localParticipant: selfAsParticipant,
    remoteParticipants: sortedPeers,
    isScreensharing,
  });

  const gridRows = layoutMode === CallLayoutMode.Spotlight ? 1 : undefined;
  const { itemsPerPage, gridColumns } =
    useResponsiveVideoGridDimensions(gridRows);
  const { itemsPerPage: waitingItemsPerPage, gridColumns: waitingGridColumns } =
    useResponsiveVideoGridDimensions();

  const isScreensharingSpotlight =
    spotlitParticipant &&
    "connectionState" in spotlitParticipant &&
    spotlitParticipant.consumers.screenshare &&
    !spotlitParticipant.consumers.screenshare.paused;

  const isVideoSpotlight =
    spotlitParticipant &&
    "connectionState" in spotlitParticipant &&
    spotlitParticipant.consumers.video &&
    !spotlitParticipant.consumers.video.paused;

  const toggleAudio = useCallback(() => {
    try {
      if (localProducers.audio?.paused) resumeProducer(ProducerLabel.audio);
      else pauseProducer(ProducerLabel.audio);
    } catch (e) {
      if (e instanceof Error && e.message !== "Not connected") {
        Sentry.captureException(e);
      }
    }
  }, [pauseProducer, resumeProducer, localProducers]);

  const toggleVideo = useCallback(() => {
    try {
      if (localProducers.video?.paused) resumeProducer(ProducerLabel.video);
      else pauseProducer(ProducerLabel.video);
    } catch (e) {
      if (e instanceof Error && e.message !== "Not connected") {
        Sentry.captureException(e);
      }
    }
  }, [pauseProducer, resumeProducer, localProducers]);

  // Without SELF_CONTROL, a participant may mute themselves but never
  // unmute — unmuting is reserved for someone with REMOTE_CONTROL (e.g. a
  // webinar host). This mirrors the "you have been unmuted by a host" flow
  // in ConnectCallConnection.tsx, and keeps a client-side self-unmute from
  // racing server-enforced mute state.
  const toggleAudioIfAble = useCallback(() => {
    if (
      isCapableOf(CallParticipantCapability.SELF_CONTROL, receivedUser) ||
      !localProducers.audio?.paused
    ) {
      toggleAudio();
    }
  }, [toggleAudio, receivedUser, localProducers]);

  const chatAvailable = callFeatures?.chat;
  // CHAT gates only *sending* — a participant without it can still open the
  // panel and read messages (CallChat already renders read-only when
  // onSend is undefined). If a facility instead wants CHAT to hide the
  // panel entirely for uncapable participants, gate the chat button on
  // canSendChat too instead of chatAvailable.
  const canSendChat =
    chatAvailable && isCapableOf(CallParticipantCapability.CHAT, receivedUser);
  const canAnswer =
    isCapableOf(CallParticipantCapability.ANSWERS, receivedUser) ||
    isCapableOf(CallParticipantCapability.ANNOUNCEMENTS, receivedUser);
  const canAsk =
    !canAnswer &&
    isCapableOf(CallParticipantCapability.QUESTIONS, receivedUser);
  const hasChatOrQAndA = canAnswer || canAsk || chatAvailable;

  const totalQuestionCount =
    webinarQuestions.length +
    visiblePeers.filter((x) => x.status.includes(UserStatus.HandRaised)).length;

  const chatUnreadCount = useUnreadMessageCount(
    messages,
    shownPanel === Panel.Chat
  );
  const qAndAUnreadCount = useUnreadEventCount(
    webinarQuestions
      .map((x) => x.timestamp)
      .concat(
        webinarAnnouncements
          .map((x) => x.timestamp)
          .concat(
            visiblePeers
              .filter((x) => x.status.includes(UserStatus.HandRaised))
              .map((x) => x.lastRaisedHand)
          )
      ),
    shownPanel === Panel.Chat
  );

  // Sound alert for new Q&A activity — only for moderators, so an attendee
  // isn't alerted about other attendees' questions/raised hands.
  useNewQAndAAlertSound(
    canAnswer
      ? webinarQuestions
          .map((x) => x.timestamp)
          .concat(
            webinarAnnouncements
              .filter((x) => x.author.id !== currentParticipant.externalId)
              .map((x) => x.timestamp)
          )
          .concat(
            visiblePeers
              .filter((x) => x.status.includes(UserStatus.HandRaised))
              .map((x) => x.lastRaisedHand)
          )
      : []
  );

  const displaySelfMuted = localProducers.audio
    ? localProducers.audio.paused
    : beginMuted;
  const displaySelfHidden = localProducers.video
    ? localProducers.video.paused
    : beginHidden;

  return (
    <Box
      sx={{
        height: 1,
        width:
          shownPanel !== Panel.None && isLargeTabletAndUp
            ? `calc(100% - ${chatWidth})`
            : "100%",
      }}
    >
      <Box sx={backdropStyle}>
        {layoutMode === CallLayoutMode.Spotlight && peerHasConnected && (
          <FloatingSelfieVideo
            user={currentParticipant}
            peerHasConnected={peerHasConnected}
            hidden={displaySelfHidden}
            muted={displaySelfMuted}
            localProducers={localProducers}
            callStatus={callStatus}
            clientStatus={clientStatus}
            connectionState={receivedUser?.connectionState}
          />
        )}
        {!isLargeTabletAndUp && (
          <Box
            sx={{
              position: "absolute",
              top: ltrTheme.spacing(3),
              right: ltrTheme.spacing(2),
              zIndex: "modal",
            }}
          >
            <Stack direction="row" spacing={2}>
              {isConfidentialCall(features) && (
                <CallConfidentialWidget features={features} />
              )}
              <CallTimerWidget
                startAt={interval.startAt}
                endAt={interval.endAt}
              />
            </Stack>
          </Box>
        )}
        <Box sx={{ width: 1, height: 1 }}>
          {layoutMode === CallLayoutMode.Grid ? (
            <VideosGrid
              remoteParticipants={sortedPeers}
              localParticipant={selfAsParticipant}
              participantsPerPage={itemsPerPage}
              gridColumns={gridColumns}
              canRemoteControl={canModerate}
              onRemoteAudioMute={canModerate ? remoteAudioMute : undefined}
              onRemoteAudioUnmute={canModerate ? remoteAudioUnmute : undefined}
              callStatus={callStatus}
              clientStatus={clientStatus}
              localProducers={localProducers}
            />
          ) : isScreensharing ? (
            <VideoParticipant
              user={currentParticipant}
              origin="local"
              audioMuted={!!localProducers.audio?.paused}
              videoPaused={localProducers.screenshare?.paused}
              audioStream={localProducers.audio?.stream}
              stream={localProducers.screenshare?.stream}
              callStatus={callStatus}
              clientStatus={clientStatus}
              userNameTag={t("Your screen")}
            />
          ) : isScreensharingSpotlight ? (
            <VideoParticipant
              user={spotlitParticipant.name}
              origin="remote"
              audioMuted={!!spotlitParticipant.consumers.audio?.paused}
              videoPaused={spotlitParticipant.consumers.screenshare?.paused}
              audioStream={spotlitParticipant.consumers.audio?.stream}
              stream={spotlitParticipant.consumers.screenshare?.stream}
              callStatus={callStatus}
              clientStatus={clientStatus}
              userNameTag={t("{{fullName}} screen", {
                fullName: spotlitParticipant.name.fullName,
              })}
            />
          ) : !peerHasConnected ? (
            <VideosGrid
              remoteParticipants={[]}
              localParticipant={selfAsParticipant}
              participantsPerPage={waitingItemsPerPage}
              gridColumns={waitingGridColumns}
              callStatus={callStatus}
              clientStatus={clientStatus}
              localProducers={localProducers}
            />
          ) : isVideoSpotlight ? (
            <>
              <VideoParticipant
                user={spotlitParticipant.name}
                origin="remote"
                audioMuted={!!spotlitParticipant.consumers.audio?.paused}
                videoPaused={spotlitParticipant.consumers.video?.paused}
                audioStream={spotlitParticipant.consumers.audio?.stream}
                stream={spotlitParticipant.consumers.video?.stream}
                callStatus={callStatus}
                clientStatus={clientStatus}
                userNameTag={spotlitParticipant.label}
                canControlAudio={
                  canModerate &&
                  !isCapableOf(
                    CallParticipantCapability.REMOTE_CONTROL,
                    spotlitParticipant
                  )
                }
                onMute={
                  canModerate && remoteAudioMute
                    ? () => remoteAudioMute(spotlitParticipant.user.id)
                    : undefined
                }
                onUnmute={
                  canModerate && remoteAudioUnmute
                    ? () => remoteAudioUnmute(spotlitParticipant.user.id)
                    : undefined
                }
                containerSx={{ borderRadius: 4 }}
              />
              <ParticipantAudio
                srcObject={spotlitParticipant.consumers.audio?.stream}
                autoPlay
              />
            </>
          ) : (
            <SpotlightPlaceholder />
          )}
          {layoutMode === CallLayoutMode.Spotlight &&
            visiblePeers
              .filter((p) => p.user.id !== spotlitParticipant?.user.id)
              .map((p) => (
                <ParticipantAudio
                  key={p.user.id}
                  srcObject={p.consumers.audio?.stream}
                  autoPlay
                />
              ))}
        </Box>
        {clientStatus === "initializing" ? (
          <StatusWidget message={t("Connecting...")} />
        ) : clientStatus === "disconnected" ? (
          <StatusWidget
            message={t(
              "You are disconnected from the call. Please check your network connection."
            )}
          />
        ) : clientStatus === "errored" ? (
          <StatusWidget
            message={t(
              "An error occurred with the call. Please try leaving and rejoining."
            )}
          />
        ) : !peerHasConnected ? (
          <StatusWidget message={t("Waiting for others to join")} />
        ) : visiblePeers.length === 0 ? (
          <StatusWidget message={t("Waiting for others to rejoin")} />
        ) : null}
        {showCaptions && captionsTimeline.length && (
          <CaptionsContainer>
            {captionsTimeline.map((segment) => (
              <CaptionBox
                key={segment.timestamp}
                user={
                  segment.user && participantsById[segment.user]
                    ? participantsById[segment.user]
                    : {
                        fullName: t("You"),
                      }
                }
                message={segment.text}
              />
            ))}
          </CaptionsContainer>
        )}
        <Box
          sx={{
            display: "flex",
            width: 1,
            position: "absolute",
            zIndex: "modal",
            left: 0,
            bottom: ltrTheme.spacing(8),
            px: 2,
            height: 0,
          }}
        >
          <Box
            sx={{
              flex: "1 1 0px",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            {isLargeTabletAndUp && (
              <Stack direction="row" spacing={2}>
                <CallTimerWidget
                  startAt={interval.startAt}
                  endAt={interval.endAt}
                />
                {isConfidentialCall(features) && (
                  <CallConfidentialWidget features={features} />
                )}
                <CallWidget
                  onClick={() =>
                    setShownPanel(
                      shownPanel === Panel.Participants
                        ? Panel.None
                        : Panel.Participants
                    )
                  }
                  sx={{
                    display: "flex",
                    cursor: "pointer",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <GroupsIcon />
                    <Box>{nonMonitorPeers.length + 1}</Box>
                  </Stack>
                </CallWidget>
              </Stack>
            )}
          </Box>
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
            <CallControls
              muted={displaySelfMuted}
              hidden={displaySelfHidden}
              captions={showCaptions}
              captionsDisabled={clientStatus !== ClientStatus.connected}
              captionsAvailable={callFeatures && callFeatures.captions}
              alerts={false}
              micDisabled={
                displaySelfMuted &&
                !isCapableOf(
                  CallParticipantCapability.SELF_CONTROL,
                  receivedUser
                )
              }
              micDisabledHelperText={t("Someone else needs to unmute you")}
              onToggleAudio={toggleAudioIfAble}
              onToggleVideo={
                isCapableOf(
                  CallParticipantCapability.SELF_CONTROL,
                  receivedUser
                )
                  ? toggleVideo
                  : undefined
              }
              isScreensharing={isScreensharing}
              screenshareDisabled={
                peerIsScreensharing || clientStatus !== ClientStatus.connected
              }
              onToggleScreenshare={
                isCapableOf(CallParticipantCapability.SCREENSHARE, receivedUser)
                  ? toggleScreenshare
                  : undefined
              }
              layoutMode={layoutMode}
              layoutToggleDisabled={isScreensharing || peerIsScreensharing}
              onToggleLayout={
                visiblePeers.length >= 2 ? toggleLayoutMode : undefined
              }
              onToggleCaptions={() => {
                if (toggleCaption) {
                  const newState = !showCaptions;
                  toggleCaption(newState).catch((e: unknown) => {
                    if (e instanceof Error && e.message !== "Not connected") {
                      Sentry.captureException(e);
                    }
                  });
                  setShowCaptions(newState);
                }
              }}
              backgroundSelectionDisabled={
                clientStatus !== ClientStatus.connected || !canChooseBackground
              }
              onToggleBackgroundSelection={
                segmenterStatus === "failed" ||
                !onSetCustomBackground ||
                isAmeelioTablet()
                  ? undefined
                  : () =>
                      setShownPanel(
                        shownPanel === Panel.Backgrounds
                          ? Panel.None
                          : Panel.Backgrounds
                      )
              }
              onToggleChat={
                hasChatOrQAndA
                  ? () =>
                      setShownPanel(
                        shownPanel === Panel.Chat ? Panel.None : Panel.Chat
                      )
                  : undefined
              }
              messageCount={
                canAnswer
                  ? totalQuestionCount
                  : canAsk
                    ? qAndAUnreadCount
                    : chatUnreadCount
              }
              onLeave={onLeave}
              leaveCallLabel={t("Leave call")}
              leaveCallDisabled={
                leaving || clientStatus === ClientStatus.initializing
              }
              styleOverrides={{
                transform: "translateY(-50%)",
              }}
            />
          </Box>
          <Box sx={{ flex: "1 1 0px" }}>&nbsp;</Box>
        </Box>
      </Box>
      {shownPanel !== Panel.None && (
        <CallPanel>
          {shownPanel === Panel.Participants && (
            <CallParticipants
              isHost={canModerate}
              currentParticipant={currentParticipant}
              peers={nonMonitorPeers}
              toggleAudio={toggleAudio}
              audioPaused={!!localProducers.audio?.paused}
              remoteAudioMute={remoteAudioMute}
              remoteAudioUnmute={remoteAudioUnmute}
              meetingUsers={participantsById}
              onClose={() => setShownPanel(Panel.None)}
            />
          )}
          {shownPanel === Panel.Chat &&
            canAnswer &&
            remoteAudioMute &&
            remoteAudioUnmute && (
              <WebinarHostQAndA
                muteSounds
                participants={sortedPeers}
                remoteLowerHand={remoteLowerHand}
                dismissWebinarQuestion={dismissWebinarQuestion}
                remoteAudioUnmute={remoteAudioUnmute}
                remoteAudioMute={remoteAudioMute}
                currentUserId={currentParticipant.externalId}
                correspondents={correspondents}
                announcements={webinarAnnouncements}
                sendWebinarAnnouncement={sendWebinarAnnouncement}
                dismissWebinarAnnouncement={dismissWebinarAnnouncement}
                questions={webinarQuestions}
                onClose={() => setShownPanel(Panel.None)}
              />
            )}
          {shownPanel === Panel.Chat && canAsk && receivedUser && (
            <WebinarQAndA
              muteSounds
              raiseHand={raiseHand}
              lowerHand={lowerHand}
              handRaised={receivedUser.status.includes(UserStatus.HandRaised)}
              currentUserId={currentParticipant.externalId}
              correspondents={correspondents}
              questions={webinarQuestions}
              announcements={webinarAnnouncements}
              onSend={
                callStatus === "live" && clientStatus === ClientStatus.connected
                  ? async (message) => {
                      await sendWebinarQuestion(message).catch((e: unknown) =>
                        Sentry.captureException(e)
                      );
                    }
                  : undefined
              }
              onClose={() => setShownPanel(Panel.None)}
            />
          )}
          {shownPanel === Panel.Chat &&
            !canAnswer &&
            !canAsk &&
            chatAvailable && (
              <CallChat
                callFeatures={features}
                facilityName={facilityName}
                currentParticipantId={currentParticipant.externalId}
                participants={participants}
                messages={messages}
                onSend={
                  canSendChat &&
                  callStatus === "live" &&
                  clientStatus === ClientStatus.connected
                    ? async (message) => {
                        await sendMessage(message).catch((e: unknown) =>
                          Sentry.captureException(e)
                        );
                      }
                    : undefined
                }
                onClose={() => setShownPanel(Panel.None)}
              />
            )}
          {shownPanel === Panel.Backgrounds && onSetCustomBackground && (
            <CallBackgroundSelector
              value={customBackground || CustomBackground.NONE}
              onChange={(c: CustomBackground) => {
                onSetCustomBackground(c);
                logClientEvent(`Setting custom background: ${c.kind}`);
              }}
              onClose={() => setShownPanel(Panel.None)}
              canClearCustomBackground={canRemoveBackground}
              canBlurBackground={canBlurBackground} // SC-6218 Blur filter on a 2D canvas isn't currently supported in Safari
            />
          )}
        </CallPanel>
      )}
    </Box>
  );
}

export default SuperVideoCallInterface;
